import { PrimeMember } from 'iam/MemberStore'
import Member from 'iam/models/Member'
import { Model } from 'infra/model'
import { VideoCall } from 'messaging/calling/VideoCall'
import {
  VideoCallParticipantDto,
  VideoCallParticipantStatusType,
} from 'messaging/dtos/video-calls'
import { action, observable, makeObservable } from 'mobx'
import { Id } from 'types/definitions'

/**
 * Participant on a call.
 */
export class VideoCallParticipant extends Model<
  VideoCallParticipantDto,
  VideoCallParticipant
> {
  userId: Id
  @observable member = Member.none
  @observable status = VideoCallParticipantStatus.Unknown

  constructor(private call: VideoCall, private primeMember: PrimeMember) {
    super()
    makeObservable(this)
  }

  /**
   * Marks the participant as connecting.
   */
  @action.bound
  markConnecting() {
    this.status = VideoCallParticipantStatus.Connecting
  }

  /**
   * Marks the participant as connected.
   */
  @action.bound
  markConnected() {
    this.status = VideoCallParticipantStatus.Connected
  }

  /**
   * Marks the participant as disconnected.
   */
  @action.bound
  markDisconnected() {
    this.status = VideoCallParticipantStatus.Disconnected
  }

  /**
   * Marks the participant as declined
   */
  @action.bound
  markDeclined() {
    this.status = VideoCallParticipantStatus.Declined
  }

  /**
   * Marks the participant as declined
   */
  @action.bound
  markMissed() {
    this.status = VideoCallParticipantStatus.Missed
  }

  /**
   * Parses attributes from the DTO.
   *
   * @param attrs
   */
  parse(
    attrs: Partial<VideoCallParticipantDto>
  ): Partial<VideoCallParticipant> {
    return {
      userId: attrs.user_id,
      // When parsing, only use the passed-in status if the current status
      // is not connecting or connected. This is because the server DTO does not
      // have that information, so we need to trust what we have.
      // The only way the server can drive the connection state is via
      // specific events. Those will use the `mark*` methods.
      status:
        this.status === VideoCallParticipantStatus.Connecting ||
        this.status === VideoCallParticipantStatus.Connected
          ? undefined
          : VideoCallParticipant.maybeMapParticipantStatus(attrs.status),
      member: this.primeMember({
        user_id: attrs.user_id,
        user_public_id: attrs.user_public_id,
        workspace_id: this.call.conversation.workspace.id,
      }),
    }
  }

  /**
   * Since we have a `Connecting` status locally but not on the server,
   * we need to map.
   *
   * @param status
   */
  private static maybeMapParticipantStatus(
    status: VideoCallParticipantStatusType | undefined
  ): VideoCallParticipantStatus | undefined {
    switch (status) {
      case VideoCallParticipantStatusType.Invited:
        return VideoCallParticipantStatus.Invited
      case VideoCallParticipantStatusType.Connected:
        return VideoCallParticipantStatus.Connected
      case VideoCallParticipantStatusType.Disconnected:
        return VideoCallParticipantStatus.Disconnected
      case VideoCallParticipantStatusType.Declined:
        return VideoCallParticipantStatus.Declined
      case VideoCallParticipantStatusType.Missed:
        return VideoCallParticipantStatus.Missed
      case undefined:
        return undefined
      default:
        return VideoCallParticipantStatus.Unknown
    }
  }
}

/**
 * Call participant status.
 */
export const enum VideoCallParticipantStatus {
  Unknown = 'Unknown',
  Invited = 'Invited',
  Declined = 'Declined',
  Missed = 'Missed',
  Connecting = 'Connecting',
  Connected = 'Connected',
  Disconnected = 'Disconnected',
}
